import BorderCard from "../campaigns/components/UserCampaigns/components/BorderCard";
import CardTitle from "../campaigns/components/UserCampaigns/components/CardTitle";
import SettingsCommonLayout from "./SettingsCommonLayout";
import SelectDeliverabilityField from "./Deliverability/SelectDeliverabilityField";
import { useState, useLayoutEffect } from "react";
import {
  useGetDeliverabilitySettings,
  useUpdateDeliverabilitySettings,
} from "@/hooks/useDeliverability";
import Loader from "@/shared/components/ui/Loaders/Loader";
import DomainAuthenticationCard from "./DomainAuthenticationCard";
import {
  buildFormDataFromSettings,
  computeEmailLimitSplit,
  deliverabilityConfig,
} from "@/shared/utils/settingsConfig";

const MANUAL_EMAIL_LIMIT_KEYS = [
  "dailyEmailLimit",
  "newContactEmailLimit",
  "followupEmailLimit",
];

const DeliverabilitySettings = ({ tab }) => {
  const {
    data: deliverabilitySettings,
    isLoading: isLoadingDeliverabilitySettings,
  } = useGetDeliverabilitySettings();

  const { mutate: updateDeliverabilitySettings } =
    useUpdateDeliverabilitySettings();

  const [formData, setFormData] = useState(() =>
    buildFormDataFromSettings(deliverabilitySettings),
  );

  useLayoutEffect(() => {
    if (deliverabilitySettings) {
      setFormData(buildFormDataFromSettings(deliverabilitySettings));
    }
  }, [deliverabilitySettings]);

  const handleChange = (key, value) => {
    const next = typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim())
      ? Number(value) : value;
    const patch = key === "dailyEmailLimit"
      ? { dailyEmailLimit: next, ...computeEmailLimitSplit(next) }
      : { [key]: next };
    updateDeliverabilitySettings(patch, {
      onSuccess: (savedSettings) => {
        setFormData(buildFormDataFromSettings(savedSettings));
      },
    });
  };

  return (
    <SettingsCommonLayout
      title="Deliverability"
      description="Control sending limits and pacing to protect accounts from spam filters."
      tab={tab}
    >
      {isLoadingDeliverabilitySettings ? (
        <Loader text="Loading deliverability settings..." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {deliverabilityConfig?.map((section) => (
            <div
              key={section.sectionTitle}
              className="flex h-full min-h-0 min-w-0 flex-col"
            >
              <BorderCard className="shrink-0">
                <CardTitle
                  text={section.sectionTitle}
                  description={section.sectionDescription}
                />
              </BorderCard>

              <BorderCard
                variant="bottom"
                padding="p-5 flex flex-col gap-2"
                className="min-h-0 flex-1"
              >
                {section.fields.map((field, index) => {
                  const disableManualLimits =
                    MANUAL_EMAIL_LIMIT_KEYS.includes(field.key) &&
                    formData.emailWarmupEnabled;
                  const effectiveField = disableManualLimits
                    ? { ...field, disabled: true }
                    : field;
                  return (
                    <SelectDeliverabilityField
                      key={field.key}
                      field={effectiveField}
                      value={formData[field.key]}
                      onChange={handleChange}
                      index={index}
                      length={section.fields.length}
                    />
                  );
                })}
              </BorderCard>
            </div>
          ))}
        </div>
      )}
      <DomainAuthenticationCard />
    </SettingsCommonLayout>
  );
};

export default DeliverabilitySettings;
